/**
 * Minting a public demo session (task 405).
 *
 * The demo stage's two anonymous operations, in order: fetch a proof-of-work
 * challenge, solve it here, and trade the solution for a Descope refresh JWT
 * (`docs/api-specs/dashboard-api.yaml`, `/demo/challenge` and
 * `/demo/session`). The request names nobody: which member and organisation
 * the session is for is the server's configuration, never the caller's.
 */

/** The demo stage's host, or empty where this build has no demo. */
export const DEMO_API_URL: string = import.meta.env.VITE_DEMO_API_URL ?? "";

/** The demo shop, or empty where this environment has none. */
export const DEMO_SHOP_URL: string = import.meta.env.VITE_DEMO_SHOP_URL ?? "";

export interface DemoChallenge {
  algorithm: "SHA-256";
  salt: string;
  challenge: string;
  signature: string;
  maxnumber: number;
}

export type DemoStartResult =
  | { ok: true; refreshJwt: string; expiresAt: string }
  | {
      ok: false;
      /**
       * `not_configured`: no demo in this environment (or this build).
       * `too_many`: the address has started its day's allowance.
       * `unavailable`: the service could not mint one; a later try may.
       */
      reason: "not_configured" | "too_many" | "unavailable";
    };

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

/**
 * Find the number the challenge hides, as the marketing site's scanner does
 * (`apps/landing/src/scripts/scanner-client.ts`). It yields every 500 hashes
 * so the page keeps painting while it works.
 */
export async function solveDemoChallenge(
  challenge: DemoChallenge,
): Promise<number> {
  for (let number = 0; number <= challenge.maxnumber; number++) {
    if (
      (await sha256Hex(`${challenge.salt}${number}`)) === challenge.challenge
    ) {
      return number;
    }
    if (number % 500 === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  throw new Error("The demo challenge could not be solved");
}

function refusal(status: number): DemoStartResult {
  if (status === 404) return { ok: false, reason: "not_configured" };
  if (status === 429) return { ok: false, reason: "too_many" };
  return { ok: false, reason: "unavailable" };
}

/**
 * Start a demo session. A refused solution (400) is retried once with a new
 * challenge: the likeliest cause is a challenge that expired while a
 * backgrounded tab solved it slowly.
 */
export async function startDemo(
  apiUrl: string = DEMO_API_URL,
  fetchImpl: typeof fetch = fetch,
): Promise<DemoStartResult> {
  if (!apiUrl) return { ok: false, reason: "not_configured" };

  for (let attempt = 0; attempt < 2; attempt++) {
    let challengeResponse: Response;
    try {
      challengeResponse = await fetchImpl(`${apiUrl}/demo/challenge`);
    } catch {
      return { ok: false, reason: "unavailable" };
    }
    if (!challengeResponse.ok) return refusal(challengeResponse.status);
    // A body cut off in transit, or a challenge with no answer in range, is
    // a service that did not answer properly: the visitor gets Try again.
    let challenge: DemoChallenge;
    let number: number;
    try {
      challenge = (await challengeResponse.json()) as DemoChallenge;
      number = await solveDemoChallenge(challenge);
    } catch {
      return { ok: false, reason: "unavailable" };
    }

    let sessionResponse: Response;
    try {
      sessionResponse = await fetchImpl(`${apiUrl}/demo/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge: {
            salt: challenge.salt,
            challenge: challenge.challenge,
            signature: challenge.signature,
            number,
          },
        }),
      });
    } catch {
      return { ok: false, reason: "unavailable" };
    }
    if (sessionResponse.status === 400 && attempt === 0) continue;
    if (!sessionResponse.ok) return refusal(sessionResponse.status);
    let session: { refreshJwt?: unknown; expiresAt?: unknown };
    try {
      session = (await sessionResponse.json()) as typeof session;
    } catch {
      return { ok: false, reason: "unavailable" };
    }
    if (
      typeof session.refreshJwt !== "string" ||
      typeof session.expiresAt !== "string"
    ) {
      return { ok: false, reason: "unavailable" };
    }
    return {
      ok: true,
      refreshJwt: session.refreshJwt,
      expiresAt: session.expiresAt,
    };
  }
  return { ok: false, reason: "unavailable" };
}
